import { Cause, Deferred, Effect, Exit, Ref, Scope } from "effect";

const closed = (scope: Scope.Scope): boolean => scope.state._tag === "Closed";

/** Each caller owns a private session. Root cleanup also joins every session,
 * retaining all release receipts without inspecting their private reasons. */
export const makeSessionOwner = Effect.fn("Frontend.SessionOwner.make")(function* (root: Scope.Scope) {
  const life = yield* Ref.make<{ readonly closed: boolean; readonly sessions: ReadonlyMap<object, Effect.Effect<void>> }>({ closed: false, sessions: new Map() });
  const failures = yield* Ref.make<readonly Cause.Cause<never>[]>([]);
  yield* Effect.addFinalizer(() => Effect.uninterruptible(Effect.gen(function* () {
    const current = yield* Ref.getAndUpdate(life, value => ({ ...value, closed: true }));
    yield* Effect.forEach(current.sessions.values(), close => Effect.exit(close), { discard: true });
    const retained = yield* Ref.get(failures);
    if (retained.length) return yield* Effect.failCause(Cause.fromReasons(retained.flatMap(cause => cause.reasons)));
  })));
  const open = <A, E, R>(acquire: Effect.Effect<A, E, R | Scope.Scope>): Effect.Effect<A, E, R | Scope.Scope> => Effect.uninterruptible(Effect.gen(function* () {
    const caller = yield* Scope.Scope;
    if (closed(caller) || closed(root)) return yield* Effect.interrupt;
    const owner = yield* Scope.make(), identity = {}, closing = yield* Ref.make(false), receipt = yield* Deferred.make<Exit.Exit<void>>();
    const close = Effect.uninterruptible(Effect.gen(function* () {
      if (yield* Ref.getAndSet(closing, true)) return yield* (yield* Deferred.await(receipt));
      const exit = yield* Effect.exit(Scope.close(owner, Exit.void));
      if (Exit.isFailure(exit)) yield* Ref.update(failures, current => [...current, exit.cause]);
      yield* Ref.update(life, current => { const sessions = new Map(current.sessions); sessions.delete(identity); return { ...current, sessions }; });
      yield* Deferred.succeed(receipt, exit);
      return yield* exit;
    }));
    const admitted = yield* Ref.modify(life, current => current.closed || closed(root) ? [false, current] as const : [true, { ...current, sessions: new Map(current.sessions).set(identity, close) }] as const);
    if (!admitted) { yield* close; return yield* Effect.interrupt; }
    const result = yield* Effect.exit(acquire.pipe(Effect.provideService(Scope.Scope, owner)));
    if (Exit.isFailure(result)) {
      const cleanup = yield* Effect.exit(close);
      return yield* Effect.failCause(Cause.fromReasons([...result.cause.reasons, ...(Exit.isFailure(cleanup) ? cleanup.cause.reasons : [])]));
    }
    yield* Scope.addFinalizer(caller, close);
    if (closed(caller) || closed(root)) return yield* Effect.interrupt;
    return result.value;
  }));
  return { open };
});
